import { devModeEnabled, getCurrentToken, getDevUserSub } from "@/lib/auth";
import { log } from "@/lib/log";
import type { UUID, WsEnvelope, WsEventType } from "@/lib/types";

type Listener = (envelope: WsEnvelope) => void;

interface BoardSocketOptions {
  // Override the default backoff schedule (only used by tests).
  backoffSchedule?: number[];
  heartbeatIntervalMs?: number;
  // Inject a custom WebSocket factory (tests pass a mock).
  socketFactory?: (url: string) => WebSocket;
  // Inject token resolver for tests.
  resolveAuth?: () => Promise<{ token: string | null; devSub: string | null }>;
}

const DEFAULT_BACKOFF = [1_000, 2_000, 4_000, 8_000, 15_000, 30_000];
const DEFAULT_HEARTBEAT_MS = 25_000;

export class BoardSocket {
  private readonly boardId: UUID;
  private readonly listeners = new Set<Listener>();
  private readonly backoffSchedule: number[];
  private readonly heartbeatIntervalMs: number;
  private readonly socketFactory: (url: string) => WebSocket;
  private readonly resolveAuth: () => Promise<{ token: string | null; devSub: string | null }>;
  private socket: WebSocket | null = null;
  private retryAttempt = 0;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private heartbeatTimer: ReturnType<typeof setInterval> | null = null;
  private outbox: string[] = [];
  private closed = false;

  constructor(boardId: UUID, opts: BoardSocketOptions = {}) {
    this.boardId = boardId;
    this.backoffSchedule = opts.backoffSchedule ?? DEFAULT_BACKOFF;
    this.heartbeatIntervalMs = opts.heartbeatIntervalMs ?? DEFAULT_HEARTBEAT_MS;
    this.socketFactory = opts.socketFactory ?? ((url) => new WebSocket(url));
    this.resolveAuth =
      opts.resolveAuth ??
      (async () => {
        if (devModeEnabled()) {
          return { token: null, devSub: getDevUserSub() };
        }
        return { token: await getCurrentToken(), devSub: null };
      });
  }

  subscribe(listener: Listener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  async connect(): Promise<void> {
    if (this.closed) return;
    const { token, devSub } = await this.resolveAuth();
    const params = new URLSearchParams({ board_id: this.boardId });
    // Dev bypass: backend reads `token` query as the dev sub when X-Dev-User header is absent.
    const tokenForQuery = token ?? devSub;
    if (tokenForQuery) params.set("token", tokenForQuery);
    const base = import.meta.env.VITE_WS_URL.replace(/\/$/, "");
    const url = `${base}?${params.toString()}`;
    try {
      this.socket = this.socketFactory(url);
    } catch (err) {
      log.error("WS construct failed", err);
      this.scheduleReconnect();
      return;
    }
    this.socket.addEventListener("open", this.handleOpen);
    this.socket.addEventListener("message", this.handleMessage);
    this.socket.addEventListener("close", this.handleClose);
    this.socket.addEventListener("error", this.handleError);
  }

  send(payload: { type: WsEventType | "ping"; [k: string]: unknown }): void {
    const data = JSON.stringify(payload);
    if (this.socket && this.socket.readyState === WebSocket.OPEN) {
      this.socket.send(data);
    } else {
      this.outbox.push(data);
    }
  }

  close(): void {
    this.closed = true;
    if (this.reconnectTimer) clearTimeout(this.reconnectTimer);
    if (this.heartbeatTimer) clearInterval(this.heartbeatTimer);
    this.listeners.clear();
    if (this.socket) {
      this.socket.removeEventListener("open", this.handleOpen);
      this.socket.removeEventListener("message", this.handleMessage);
      this.socket.removeEventListener("close", this.handleClose);
      this.socket.removeEventListener("error", this.handleError);
      try {
        this.socket.close(1000, "client closing");
      } catch {
        /* ignore */
      }
      this.socket = null;
    }
  }

  private handleOpen = (): void => {
    this.retryAttempt = 0;
    this.flushOutbox();
    this.startHeartbeat();
  };

  private handleMessage = (event: MessageEvent<string>): void => {
    let envelope: WsEnvelope | null = null;
    try {
      envelope = JSON.parse(event.data) as WsEnvelope;
    } catch (err) {
      log.warn("Bad WS payload", err);
      return;
    }
    if (!envelope || typeof envelope.type !== "string") return;
    for (const listener of this.listeners) {
      try {
        listener(envelope);
      } catch (err) {
        log.error("WS listener threw", err);
      }
    }
  };

  private handleClose = (event: CloseEvent): void => {
    this.stopHeartbeat();
    // 1008 = policy violation (server refused: no board access — e.g. a guest,
    // who gets board data via the REST GET, not the socket). Don't reconnect-loop
    // on a decision the server won't reverse.
    if (event.code === 1008) {
      this.closed = true;
      return;
    }
    if (!this.closed) {
      this.scheduleReconnect();
    }
  };

  private handleError = (event: Event): void => {
    log.warn("WS error", event);
  };

  private startHeartbeat(): void {
    this.stopHeartbeat();
    this.heartbeatTimer = setInterval(() => {
      this.send({ type: "ping" });
    }, this.heartbeatIntervalMs);
  }

  private stopHeartbeat(): void {
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
  }

  private flushOutbox(): void {
    if (!this.socket || this.socket.readyState !== WebSocket.OPEN) return;
    const pending = this.outbox.splice(0, this.outbox.length);
    for (const msg of pending) this.socket.send(msg);
  }

  private scheduleReconnect(): void {
    if (this.closed) return;
    const idx = Math.min(this.retryAttempt, this.backoffSchedule.length - 1);
    const delay = this.backoffSchedule[idx] ?? 30_000;
    this.retryAttempt += 1;
    if (this.reconnectTimer) clearTimeout(this.reconnectTimer);
    this.reconnectTimer = setTimeout(() => {
      void this.connect();
    }, delay);
  }
}
